import React from "react";
import styled, { keyframes } from "styled-components";

// Lightweight toast provider. Single visible toast at a time, auto-dismiss
// after `duration` ms (default 2200), positioned bottom-right of the
// viewport. Used for "Komentar dilaporkan", "Komentar dikirim",
// "Bookmark disimpan", etc. — confirmation only, not an alert system.
//
// Usage:
//   const { showToast } = useToast();
//   showToast({ tone: "success", message: "Komentar dikirim" });
//
// Tones: "success" (default), "info", "danger".

const ToastContext = React.createContext({ showToast: () => {} });

export function useToast() {
  return React.use(ToastContext);
}

export default function ToastProvider({ children }) {
  const [toasts, setToasts] = React.useState([]);

  const showToast = React.useCallback((opts) => {
    const id = Math.random().toString(36).slice(2);
    const toast = {
      id,
      tone: opts?.tone || "success",
      message: opts?.message || "",
      duration: opts?.duration || 2200,
    };
    setToasts((prev) => [...prev, toast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, toast.duration);
  }, []);

  const value = React.useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext value={value}>
      {children}
      <Stack aria-live="polite" aria-atomic="true">
        {toasts.map((t) => (
          <Toast key={t.id} $tone={t.tone}>
            <Dot $tone={t.tone} aria-hidden="true" />
            <Message>{t.message}</Message>
          </Toast>
        ))}
      </Stack>
    </ToastContext>
  );
}

const enter = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Stack = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 80;
  pointer-events: none;

  @media screen and (max-width: 720px) {
    right: 12px;
    bottom: 12px;
    left: 12px;
    align-items: stretch;
  }
`;

const Toast = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 12px;
  background: ${({ $tone }) =>
    $tone === "danger" ? "#fef2f2" : $tone === "info" ? "#eff6ff" : "#ecfdf5"};
  border: 1px solid
    ${({ $tone }) =>
      $tone === "danger"
        ? "#fecaca"
        : $tone === "info"
          ? "#bfdbfe"
          : "#a7f3d0"};
  color: ${({ $tone }) =>
    $tone === "danger" ? "#991b1b" : $tone === "info" ? "#1d4ed8" : "#047857"};
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.12);
  pointer-events: auto;
  animation: ${enter} 180ms ease-out;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  max-width: 360px;

  [data-theme="dark"] & {
    background: ${({ $tone }) =>
      $tone === "danger"
        ? "rgba(220, 38, 38, 0.18)"
        : $tone === "info"
          ? "rgba(29, 78, 216, 0.18)"
          : "rgba(16, 185, 129, 0.18)"};
    border-color: ${({ $tone }) =>
      $tone === "danger"
        ? "rgba(220, 38, 38, 0.40)"
        : $tone === "info"
          ? "rgba(29, 78, 216, 0.40)"
          : "rgba(16, 185, 129, 0.40)"};
    color: #fff;
  }
`;

const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $tone }) =>
    $tone === "danger" ? "#dc2626" : $tone === "info" ? "#1d4ed8" : "#10b981"};
  flex-shrink: 0;
`;

const Message = styled.span`
  white-space: pre-wrap;
`;
